<template>
  <div class="ca-datepicker" :class="{ 'ca-datepicker--tabla': enTabla, 'ca-datepicker--disabled': disabled }">
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      :disabled="disabled"
      :nudge-bottom="offsetY"
      :nudge-right="offsetX"
      transition="slide-x-transition"
      offset-overflow
      @input="onInputMenu($event)"
      :content-class="contentClass"
    >
      <template v-slot:activator="{ on }">
        <v-text-field
          class="ca-datepicker-input"
          v-on="on"
          v-model="fecha_format"
          :rules="required ? [reglas.required, ...rules] : rules"
          readonly
          :label="label"
          :disabled="disabled"
          :class="{ 'label-active': labelFijo, 'ca-datepicker-input--filled': !!fecha_format }"
          :prepend-icon="!innerIcon ? 'event' : null"
          :prepend-inner-icon="innerIcon ? 'event' : null"
          :error="error"
          :error-messages="errorMsj ? errorMsj : []"
          :hint="hint"
          clear-icon="cancel"
          :clearable="clearable && !disabled"
          :persistent-hint="!disabled"
          :loading="loading"
          :hide-details="hideDetails"
          @click:clear="$emit('clear')"
        >
          <template :slot="innerIcon ? 'prepend-inner' : 'prepend'">
            <v-icon v-on="on">event</v-icon>
          </template>
        </v-text-field>
      </template>
      <v-date-picker
        :range="rango"
        ref="picker"
        v-model="date"
        @input="changeDate($event)"
        :min="min"
        :max="max"
        locale="es-ar"
        :type="type"
        :allowed-dates="funcionFechasHabilitadas"
        :disabled="disabled"
        no-title
        scrollable
        :show-current="destacarFecha">
        <span v-if="rango" class="observacion-text">El rango seleccionado debe ser de <span>{{diasRango}}</span> días máximo.</span>
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
import {capitalize, isEqual} from 'lodash';
import { formatFecha } from './vue_filters';

export default {
  props: {
    // PROPS BASE
    label: {type: String, default: ''},
    labelFijo: {type: Boolean, default: false},
    fecha: {type: String, default: ''},
    rangoFechas: {type: Array, default: () => []},
    hint: {type: String, default: null},
    format: {type: String, default: 'DD/MM/YYYY'},
    disabled: {type: Boolean, default: false},
    type: {type: String, default: 'date'}, // 'date' | 'month'
    clearable: {type: Boolean, default: false},
    loading: {type: Boolean, default: false},
    rango: {type: Boolean, default: false}, // Indica si se desea la seleccion de rangos de fechas
    diasRango: { type: Number, default: 10}, // Rango de dias a seleccionar

    // MODOS DE VISUALIZACIÓN
    enTabla: {type: Boolean, default: false}, // Modifica el estilo del textfield para mejor visualización en tablas

    // PROPS DE VALIDACIÓN
    funcionFechasHabilitadas: {type: Function, default: null},
    required: {type: Boolean, default: false},
    min: {type: String, default: ''},
    max: {type: String, default: ''},
    error: {type: Boolean, default: false},
    errorMsj: {type: String, default: null},
    rules: {type: Array, default: () => []},

    // PROPS DE VISUALIZACIÓN
    innerIcon: {type: Boolean, default: false},
    destacarFecha: {type: String, default: null}, // Fecha a destacar (se mapea a showCurrent de datepicker)
    fechaInicialOculta: {type: Boolean, default: false},
    hideDetails: {type: Boolean, default: false},
    comenzarPorAnios: {type: Boolean, default: false},
    contentClass: {type: String, default: undefined},
  },
  data() {
    return {
      menu: false,
      date: this.rango ? this.rangoFechas : this.fecha, // copia local de la fecha
      fechaInicialOculta_copy: this.fechaInicialOculta,
      reglas: {
        required: value => (!!value && !this.fechaInicialOculta_copy) || 'Campo requerido'
      }
    };
  },
  computed: {
    fecha_format: {
      get() {
        if (!this.rango) {
          return !this.fechaInicialOculta_copy
            ? capitalize(formatFecha(this.date, this.format))
            : '';
        } else if (this.date.length > 0) {
          // Si es la seleccion de fechas por rango entonces muestro las dos fechas
          return this.date.length === 1 ? formatFecha(this.date[0], this.format)
            : formatFecha(this.date[0], this.format) + ' - ' + formatFecha(this.date[1], this.format);
        }
        return '';
      },
      set(fecha) { //Soluciona Vue warning de setear computed property (por tenerla como v-model)
        if (!fecha) {
          this.$emit('update:fecha', fecha);
        }
      }
    },
    offsetY() {
      return this.enTabla ? 24 : 45;
    },
    offsetX() {
      return this.enTabla ? 24 : 32;
    },
  },
  watch: {
    fecha(fecha) {
      // Modifica copia local si cambia prop
      this.date = fecha;
    },
    fechaInicialOculta(val) {
      this.fechaInicialOculta_copy = val;
    },
  },
  methods: {
    changeDate(fecha) {
      if (!this.rango) {
        this.menu = false; // Cierra datepicker
        this.$emit('update:fecha', fecha);
        this.$emit('update:fechaInicialOculta', false);
        this.fechaInicialOculta_copy = false;
      } else {
        // Al cambiar la fecha chequeo que la validez del rango
        this.checkRango(fecha);
      }
    },
    onInputMenu(value) {
      if (value && this.comenzarPorAnios) {
        setTimeout(() => (this.$refs.picker.activePicker = 'YEAR'));
      }
    },

    /**
       * Este método calcula que el rango sea correcto, es decir, que tenga un maximo de días definido en diasRango.
       * Si el valor pasado por parámetro es un rango desde-hasta entonces:
       *  -> Verifico si la fecha desde es menor a la fecha hasta (es decir, se seleccionaron al revés), si es así entonces
       * calculo cual es la fecha máxima hacia atrás del desde y swappeo las fechas.
       * Sino:
       * -> Calculo la fecha maxima desde el desde hacia adelante y seteo el hasta.
       */
    checkRango(fecha) {
      if (fecha.length === 2) {
        if (new Date(fecha[1]) < new Date(fecha[0])) {
          fecha[1] = formatFecha(this.calcularDias(fecha[0], fecha[1], false), 'YYYY-MM-DD');
          let aux = fecha[1];
          fecha[1] = fecha[0];
          fecha[0] = aux;
        } else {
          fecha[1] = formatFecha(this.calcularDias(fecha[0], fecha[1], true), 'YYYY-MM-DD');
        }
      }
      this.$emit('update:fecha', fecha);
    },

    /**
       * Este método calcula la fecha maxima del rango a partir de la primera fecha seleccionada.
       * Toma en cuenta si las fechas se seleccionaron de manera invertida (primero el hasta y luego el desde).
       *
       * @param fecha_inicio: primer fecha seleccionada, fecha_fin: segunda fecha seleccionada,
       * sumo_dias: indica si se deben sumar la cantidad de dias de diasRango o restar (es decir, las fechas se seleccionaron al revés).
       */
    calcularDias(fecha_inicio, fecha_fin, sumo_dias) {
      let fecha_desde = new Date(fecha_inicio);
      fecha_desde.setDate(fecha_desde.getDate() + 1);

      let fecha_hasta = new Date(fecha_fin);
      fecha_hasta.setDate(fecha_hasta.getDate() + 1);

      let fecha_maxima = fecha_desde;
      // Si primero se selecciono el desde y luego el hasta sumo la cantidad de dias de diasRango
      // Caso contrario, resto la cantidad de dias de diasRango
      sumo_dias ? fecha_maxima.setDate(fecha_maxima.getDate() + this.diasRango)
        : fecha_maxima.setDate(fecha_maxima.getDate() - this.diasRango);

      // Si primero se selecciono el desde y luego el hasta y la fecha maxima es mayor al hasta -> devuelve fecha hasta
      // Si primero se selecciono el hasta y luego el desde y la fecha maxima es menor al hasta -> devuelve fecha hasta
      // Si fecha maxima y la fecha hasta son iguales -> devuelve fecha hasta
      // Sino -> devuelve fecha maxima
      return (fecha_maxima > fecha_hasta && sumo_dias)
          || (fecha_maxima < fecha_hasta && !sumo_dias)
          || isEqual(fecha_maxima, fecha_hasta) ? fecha_hasta : fecha_maxima;
    }
  }
};
</script>

<style lang="scss">

  .ca-datepicker {
    .v-input__icon--clear .v-icon {
      font-size: 18px;
      &:hover {
        color: color(rojo);
      }
    }

    .ca-datepicker-input {
      &.error--text {
        .v-text-field__slot input[readonly] {
          border-color: color(rojo);
          background-color: color(blanco);
        }
      }
    }

    // MODIFICADORES

    &:not(.ca-datepicker--disabled) { // Cuando el datepicker no está deshabilitado el cursor se modifica a pointer (previendo interacción de user)
      input {
        cursor: pointer !important;
      }
    }

    &--disabled { // Cuando el datepicker está deshabilitado se bloquea el borde inferior al color normal de deshabilitado para evitar que ante hover se ponga oscuro.
      .v-input__slot:before {
        border-color: color(gris-claro-secund) !important;
      }
      .v-icon {
        cursor: default !important;
      }
    }

    &--tabla { // Estilos de input en tabla.
      height: 23px;
      .ca-datepicker-input {
        padding-top: 0;
      }
      .v-menu__activator,
      .v-input__control,
      .v-input__icon {
        height: 23px;
      }

      .v-input,
      .v-input__prepend-outer {
        margin: 0;
      }

      .v-icon {
        font-size: 19px;
      }

      .v-messages.theme--light {
        display: none;
      }

      input[readonly] {
        text-align: center;
        height: 23px;
        border-radius: 2px;
        border: 1px solid color(celeste-claro);
        background-color: color(gris-claro);
        &:hover {
          background-color: color(blanco);
        }
      }
      .v-icon {
        color: color(celeste-claro);
      }

      .v-input__slot {
        &:before, :after {
          display: none;
        }
      }

      .ca-datepicker-input--filled {
        input {
          border-color: color(celeste) !important;
          background-color: color(blanco) !important;
        }
        .v-icon {
          color: color(celeste) !important;
        }
      }

    }

  }
  .observacion-text {
    font-size: 12px;
    color: color(texto-gris);
  }

</style>
